import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  BookOpen,
  Search,
  ChevronRight,
  ArrowLeft,
  Link2,
  LifeBuoy,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import { bundledHelp, findHelp, resolveHelp, searchHelp, type ActiveHelpPackage } from "../../shared/help.js";
import { api } from "../api.js";
import "./panels.css";
import "./help.css";
export default function HelpPanel({
  topic = "getting-started",
  onTopicChange,
}: {
  topic?: string;
  onTopicChange?: (id: string) => void;
}) {
  const [current, setCurrent] = useState(topic);
  const [query, setQuery] = useState("");
  const [showIndex, setShowIndex] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  useEffect(() => setCurrent(topic), [topic]);
  const [content, setContent] = useState<ActiveHelpPackage>({ ...bundledHelp, source: "bundled" });
  const [refreshError, setRefreshError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const requestId = useRef(0);
  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    try {
      const next = await api<ActiveHelpPackage>("/help/package");
      if (id !== requestId.current) return;
      setContent(next);
      setRefreshError("");
    } catch {
      if (id === requestId.current)
        setRefreshError("Help could not be refreshed. Showing the last available compatible guide.");
    } finally {
      if (id === requestId.current) setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
    const onRefresh = () => { void refresh(); };
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void refresh();
    }, 15000);
    window.addEventListener("focus", onRefresh);
    window.addEventListener("dataglow-help-changed", onRefresh);
    return () => {
      requestId.current += 1;
      window.clearInterval(timer);
      window.removeEventListener("focus", onRefresh);
      window.removeEventListener("dataglow-help-changed", onRefresh);
    };
  }, [refresh]);
  const resolution = resolveHelp(current, content);
  const article = resolution.article;
  const categories = useMemo(
    () => [...new Set(content.index.map((id) => findHelp(id, content).category))],
    [content],
  );
  const results = useMemo(() => {
    const matches = new Set(searchHelp(query, content).map((item) => item.id));
    return content.index.filter((id) => matches.has(id)).map((id) => findHelp(id, content));
  }, [query, content]);
  function open(id: string) {
    setHistory((h) => [...h, current]);
    setCurrent(id);
    onTopicChange?.(id);
    setShowIndex(false);
    setQuery("");
  }
  return (
    <div className="dgx-help" data-testid="help-panel">
      <div className="dgx-panel-heading">
        <span className="dgx-eyebrow">
          <BookOpen size={15} /> PRODUCT GUIDE
        </span>
        <button className="dgx-link" onClick={() => setShowIndex(!showIndex)}>
          {showIndex ? "Close index" : "Topic index"}
        </button>
      </div>
      <div className="dgx-help-version" data-testid="help-package-version">
        <span>{content.manifest.release} · Help revision {content.manifest.revision}</span>
        <button className="dgx-link" onClick={() => void refresh()} disabled={refreshing} aria-label="Refresh Help content">
          <RefreshCw size={13} /> {refreshing ? "Refreshing…" : "Refresh content"}
        </button>
      </div>
      <p className="dgx-help-compatibility">Compatible with product {content.manifest.productVersion} · {content.source === "installed" ? "Organization package" : "Bundled package"}</p>
      {(refreshError || content.fallbackReason) && <p role="status" className="dgx-help-notice">{refreshError || content.fallbackReason}</p>}
      <label className="dgx-search">
        <Search size={16} />
        <input
          aria-label="Search product help"
          placeholder="Find a feature or resolve an issue"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {(showIndex || query) && (
        <nav className="dgx-help-index" aria-label="Help topics">
          {categories.map((category) => {
            const items = results.filter((a) => a.category === category);
            return items.length ? (
              <section key={category}>
                <h4>{category}</h4>
                {items.map((a) => (
                  <button
                    key={a.id}
                    className={a.id === current ? "active" : ""}
                    onClick={() => open(a.id)}
                  >
                    {a.title}
                    <ChevronRight size={13} />
                  </button>
                ))}
              </section>
            ) : null;
          })}
          {!results.length && (
            <p>No matching topic. Try "keys", "save", "API" or "layout".</p>
          )}
        </nav>
      )}
      {!query && (
        <article className="dgx-help-article">
          {history.length > 0 && (
            <button
              className="dgx-link"
              onClick={() => {
                const id = history.at(-1)!;
                setHistory(history.slice(0, -1));
                setCurrent(id);
                onTopicChange?.(id);
              }}
            >
              <ArrowLeft size={13} /> Previous topic
            </button>
          )}
          {!resolution.exact && <p role="status" className="dgx-help-notice">Detailed guidance for “{resolution.requestedContext}” is unavailable. Showing {article.title}.</p>}
          <p className="dgx-eyebrow">{article.category}</p>
          <h2>{article.title}</h2>
          <p className="dgx-lead">{article.summary}</p>
          <h3>How to do it</h3>
          <ol className="dgx-steps">
            {article.steps.map((step, i) => (
              <li key={i}>
                <span>{i + 1}</span>
                <p>{step}</p>
              </li>
            ))}
          </ol>
          {article.fields?.length && (
            <details className="dgx-details">
              <summary>
                Fields and expected input <span>{article.fields.length}</span>
              </summary>
              <dl>
                {article.fields.map((f) => (
                  <div key={f.name}>
                    <dt>{f.name}</dt>
                    <dd>
                      {f.description}
                      {f.example && <code>{f.example}</code>}
                    </dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
          {article.troubleshooting?.length && (
            <details className="dgx-details" open>
              <summary>
                <LifeBuoy size={15} /> Resolve an issue
              </summary>
              {article.troubleshooting.map((t) => (
                <div className="dgx-help-issue" key={t.symptom}>
                  <h4>{t.symptom}</h4>
                  <p>{t.resolution}</p>
                </div>
              ))}
            </details>
          )}
          <h3 className="dgx-related-title">
            <Link2 size={14} /> Related topics
          </h3>
          <div className="dgx-related">
            {article.related.map((id) => (
              <button key={id} onClick={() => open(id)}>
                {findHelp(id, content).title}
                <ChevronRight size={13} />
              </button>
            ))}
          </div>
          <p className="dgx-footnote">
            <CheckCircle2 size={13} /> Available with the assistant switched
            off. {content.manifest.release} guide; check Capabilities for verified support.
          </p>
        </article>
      )}
    </div>
  );
}
